import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Edit, Image, Package, X, Upload, DollarSign, Link as LinkIcon, ExternalLink, AlertCircle, Crown, ArrowLeft, Sparkles } from 'lucide-react';
import Footer from '../components/Footer';
import LinkifyText from '../components/LinkifyText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrandPortfolioPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'tops',
    price: '',
    affiliate_link: '',
    image_base64: ''
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Check if user is on Pro/Enterprise plan
  const isProPlan = user?.plan_id === 'brand_enterprise';

  const fetchPlanLimits = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/plan-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlanLimits(data);
      }
    } catch (err) {
      console.error('Failed to fetch plan limits:', err);
    }
  }, [token]);

  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && user?.user_type === 'brand') {
      fetchPortfolio();
      fetchPlanLimits();
    }
  }, [fetchPortfolio, fetchPlanLimits, token, user]);

  const isAtLimit = planLimits && planLimits.current_portfolio_items >= planLimits.max_portfolio_items;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image_base64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          price: formData.price ? parseFloat(formData.price) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setItems(prev => [...prev, data.item]);
        setShowModal(false);
        resetForm();
        fetchPlanLimits(); // Refresh limits
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to add item');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`${API_URL}/api/portfolio/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setItems(prev => prev.filter(item => item.id !== itemId));
        fetchPlanLimits(); // Refresh limits after delete
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'tops',
      price: '',
      affiliate_link: '',
      image_base64: ''
    });
    setEditingItem(null);
  };

  const categories = [
    { value: 'tops', label: 'Tops' },
    { value: 'bottoms', label: 'Bottoms' },
    { value: 'dresses', label: 'Dresses' },
    { value: 'outerwear', label: 'Outerwear' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'shoes', label: 'Shoes' }
  ];

  // Handle auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    navigate('/login');
    return null;
  }

  if (user?.user_type !== 'brand') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Portfolio is for Brands Only</h2>
          <p className="text-gray-500 mt-2">Upgrade to a Brand plan to manage your clothing portfolio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center space-x-2 cursor-default">
            <Sparkles className="w-6 h-6 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </div>
        </div>
      </header>

      <div className="flex-1 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Clothing Portfolio {planLimits && `(${planLimits.current_portfolio_items}/${planLimits.max_portfolio_items})`}
              </h1>
              <p className="text-gray-600 mt-1">Manage your brand's clothing items</p>
            </div>
            {!isAtLimit ? (
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                data-testid="add-portfolio-item-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Item
              </button>
            ) : !isProPlan ? (
              <Link
                to="/subscription"
                className="flex items-center px-6 py-3 bg-amber-100 text-amber-700 rounded-xl font-semibold hover:bg-amber-200 transition-all"
              >
                <Crown className="w-5 h-5 mr-2" />
                Upgrade for More
              </Link>
            ) : null}
          </div>

          {/* Limit Warning - Only show upgrade option for non-Pro users */}
          {isAtLimit && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center text-amber-700">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>
                You've reached your portfolio limit ({planLimits?.max_portfolio_items} items). 
                {isProPlan 
                  ? ' Delete existing items to add more.' 
                  : ' Delete existing items or upgrade your plan to add more.'}
              </span>
            </div>
          )}

        {/* Portfolio Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Items Yet</h3>
            <p className="text-gray-500 mb-6">Start building your portfolio by adding clothing items</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold"
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((item) => (
              <div 
                key={item.id} 
                className={`bg-white rounded-2xl shadow-lg overflow-hidden group ${item.affiliate_link ? 'cursor-pointer' : ''}`}
                data-testid={`portfolio-item-${item.id}`}
                onClick={() => {
                  if (item.affiliate_link) {
                    window.open(item.affiliate_link, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <div className="aspect-[3/4] bg-gray-50 relative">
                  {(item.image_url || item.cloudinary_url || (item.image_base64 && item.image_base64.length > 100)) ? (
                    <img
                      src={item.image_url || item.cloudinary_url || (item.image_base64.startsWith('data:') ? item.image_base64 : `data:image/png;base64,${item.image_base64}`)}
                      alt={item.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-300"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  {/* Overlay actions - always visible delete button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 z-10 shadow-md"
                    data-testid={`delete-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {item.affiliate_link && (
                    <a
                      href={item.affiliate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 left-2 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 z-10 shadow-md"
                      title="Open product link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-800 truncate">{item.name}</h3>
                      <span className="text-xs text-gray-500 capitalize">{item.category}</span>
                    </div>
                    {item.price && (
                      <span className="text-rose-500 font-semibold flex-shrink-0">${item.price}</span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2 break-all">
                      <LinkifyText text={item.description} />
                    </p>
                  )}
                  {item.affiliate_link && (
                    <div className="flex items-center mt-2 text-xs text-blue-500">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      <span>Click to view product</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingItem ? 'Edit Item' : 'Add New Item'}
                </h2>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {error && (
                  <div data-testid="portfolio-error-display">
                    {(error.toLowerCase().includes('limit') || error.toLowerCase().includes('upgrade')) ? (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-4 rounded-lg text-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <span className="font-semibold">Portfolio Limit Reached</span>
                        </div>
                        <p className="mb-3">{error}</p>
                        {!isProPlan && (
                          <Link
                            to="/subscription"
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all"
                            data-testid="portfolio-upgrade-btn"
                          >
                            <Crown className="w-4 h-4 mr-2" />
                            Upgrade Plan
                          </Link>
                        )}
                      </div>
                    ) : (
                      <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}
                  </div>
                )}

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Image *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
                    {formData.image_base64 ? (
                      <div className="relative">
                        <img
                          src={formData.image_base64}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image_base64: '' }))}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                        <span className="text-gray-600">Click to upload image</span>
                        <span className="block text-xs text-gray-400 mt-1">High-quality product photo on a clean background</span>
                        <span className="block text-xs text-gray-300 mt-1">PNG, JPG up to 5MB</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          data-testid="portfolio-image-upload"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                    placeholder="e.g., Summer Floral Dress"
                    data-testid="portfolio-item-name"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                    data-testid="portfolio-item-category"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                    placeholder="Describe your product..."
                    data-testid="portfolio-item-description"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (USD)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                      placeholder="49.99"
                      data-testid="portfolio-item-price"
                    />
                  </div>
                </div>

                {/* Affiliate Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Link
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={formData.affiliate_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, affiliate_link: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-400 focus:border-transparent"
                      placeholder="https://yourstore.com/product"
                      data-testid="portfolio-item-link"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={uploading || !formData.name || !formData.image_base64}
                  className="w-full py-3 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="portfolio-submit-btn"
                >
                  {uploading ? 'Adding...' : (editingItem ? 'Update Item' : 'Add Item')}
                </button>
              </form>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Footer */}
      <Footer isAuthenticated={true} isProPlan={isProPlan} />
    </div>
  );
}
